import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { LoaderCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { H6 } from "@/components/ui/h6";
import { EvolveWorldview } from "@/api/personality";
import { useToast } from "@/hooks/use-toast";
import { Persona } from "@/api/personas.ts";
import { Textarea } from "../ui/textarea";
import { useAuth } from "@/hooks/use-auth.tsx";
import { Role } from "../AuthorizationProvider";

interface WorldviewModalProps {
  personaId: string;
  personaData: Persona;
  authenticated: boolean;
}

export function formatWorldviewText(text: string | undefined) {
  if (!text) return null;
  const lines = text.split("\n");
  const processedLines = lines.map((line, index) => {
    const trimmedLine = line.trim();
    if (
      /^[IVXLCDM]+\. .*$/.test(trimmedLine) ||
      /^[A-Z& ]{2,}$/.test(trimmedLine)
    ) {
      return (
        <div key={index}>
          <H6 className="font-semibold">{trimmedLine}</H6>
        </div>
      );
    } else {
      return (
        <div key={index}>
          <br />
          <p>{trimmedLine}</p>
        </div>
      );
    }
  });
  return processedLines;
}

export function WorldviewModal({
  personaId,
  personaData,
  authenticated,
}: WorldviewModalProps) {
  const [showWorldviewModal, setShowWorldviewModal] = useState(false);
  const [isEvolvingWorldview, setIsEvolvingWorldview] = useState(false);
  const [worldviewFeedbackText, setWorldviewFeedbackText] = useState("");
  const [isWorldviewEvolving, setIsWorldviewEvolving] = useState(false);
  const [apiCallCompleted, setApiCallCompleted] = useState(false);
  const [stepsCompleted, setStepsCompleted] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const { toast } = useToast();
  const { hasAtLeastRole } = useAuth();
  const editPersonaDisabled =
    !personaData.persona_id ||
    (personaData.public_persona === true && !hasAtLeastRole(Role.TenantAdmin));

  const evolveSteps = [
    "Accessing consciousness framework...",
    "Mapping personality architecture...",
    "Integrating behavioral feedback...",
    "Calibrating belief systems...",
    "Crystallizing evolved perspective...",
  ];

  const handleEvolveWorldview = async () => {
    setIsWorldviewEvolving(true);
    setCurrentStepIndex(0);
    setApiCallCompleted(false);
    setStepsCompleted(false);
    try {
      await EvolveWorldview(personaId, worldviewFeedbackText);
      setApiCallCompleted(true);
    } catch {
      toast({ description: "Error evolving worldview" });
      setIsWorldviewEvolving(false);
      return;
    }
  };

  useEffect(() => {
    let interval: any;
    if (isWorldviewEvolving) {
      setCurrentStepIndex(0);
      let stepIndex = 0;
      interval = setInterval(() => {
        stepIndex++;
        if (stepIndex < evolveSteps.length) {
          setCurrentStepIndex(stepIndex);
        } else {
          clearInterval(interval);
          setStepsCompleted(true);
        }
      }, 3500);
    }
    return () => clearInterval(interval);
  }, [isWorldviewEvolving]);

  useEffect(() => {
    if (isWorldviewEvolving && apiCallCompleted && stepsCompleted) {
      setIsEvolvingWorldview(false);
      setWorldviewFeedbackText("");
      toast({ description: "Worldview evolved successfully" });
      setIsWorldviewEvolving(false);
    }
  }, [apiCallCompleted, stepsCompleted]);

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowWorldviewModal(true)}
      >
        Edit
      </Button>

      <Dialog
        open={showWorldviewModal}
        onOpenChange={(open) => {
          setShowWorldviewModal(open);
          if (!open) {
            setIsEvolvingWorldview(false);
            setWorldviewFeedbackText("");
          }
        }}
      >
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>Worldview</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {isWorldviewEvolving ? (
              <div className="flex flex-col items-center">
                <LoaderCircle className="h-12 w-12 animate-spin stroke-accent m-auto" />
                <p className="mt-4 text-center">
                  {evolveSteps[currentStepIndex]}
                </p>
              </div>
            ) : !isEvolvingWorldview ? (
              <div className="overflow-y-auto max-h-[80vh]">
                {formatWorldviewText(personaData.persona_worldview)}
              </div>
            ) : (
              <div>
                <p>
                  Time to evolve this digital consciousness. Share the traits,
                  philosophical stances, or unique characteristics that will
                  shape their worldview. Drop raw psychological insights,
                  personality dimensions, or behavioral patterns you want to
                  amplify or transform. Every input architects their reality.
                  Let's make it extraordinary.
                </p>
                <Textarea
                  value={worldviewFeedbackText}
                  onChange={(e) => setWorldviewFeedbackText(e.target.value)}
                  className="w-full h-40 mt-4"
                />
              </div>
            )}
          </DialogDescription>
          <div className="flex justify-between mt-4">
            {authenticated && !editPersonaDisabled && !isWorldviewEvolving && (
              <>
                {!isEvolvingWorldview ? (
                  <Button onClick={() => setIsEvolvingWorldview(true)}>
                    Evolve Worldview
                  </Button>
                ) : (
                  <Button
                    onClick={handleEvolveWorldview}
                    disabled={isWorldviewEvolving}
                  >
                    {isWorldviewEvolving
                      ? "Evolving..."
                      : `Evolve ${personaData.name}`}
                  </Button>
                )}
                <DialogClose asChild>
                  <Button
                    onClick={() => {
                      setShowWorldviewModal(false);
                      setIsEvolvingWorldview(false);
                      setWorldviewFeedbackText("");
                    }}
                  >
                    Close
                  </Button>
                </DialogClose>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
